import React, {useEffect, useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {isOk} from '../../../utils/apiResponse';
import {notifyOnboardingProgress} from '../../../utils/onboardingProgress';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {EditEventsModal} from '../ad_accounts/EditEventsModal';
// Los textos de la conversión viven en su propio módulo: los usan también el chip del header y las
// filas de las fuentes, y importarlos DESDE acá les arrastraba el `EditEventsModal` entero.
import {
    accountConversionLabel,
    accountHasNoEvents,
    accountNeedsCatalog,
    channelInfo,
    MODE_ALL_PRIMARY,
    monitoredConversionLabel,
    normalizeChannel,
    paidAdAccounts,
    unnamedSelectedActions,
} from './conversionLabels';
import {UnconfiguredAccountSuggestion} from './UnconfiguredAccountSuggestion';
import {anchorAttr} from '../../../guides/anchors';
import './ConversionModal.scss';

/**
 * Los nombres de las conversiones personalizadas elegidas, por cuenta: `{[account.id]: {action_id: nombre}}`.
 *
 * El payload del panel trae sólo los ids (`CUSTOM_ACTION_<n>`), y en Google TODAS las conversiones
 * son de ese tipo: sin esto, la fila de la cuenta mostraba un número donde iba el nombre (card
 * zhvaJgpF). Se pide el catálogo sólo de las cuentas que lo necesitan, con el mismo endpoint y el
 * mismo `cache: true` que la sugerencia de las cuentas sin configurar.
 *
 * Un fallo es silencio: la fila cae al genérico «Conversión personalizada», que es verdad, y el
 * modal sigue sirviendo para lo que se abrió.
 */
function useCustomActionNames(isOpen, accounts) {
    const [namesByAccount, setNamesByAccount] = useState({});
    const pending = isOpen
        ? accounts.filter((account) => account.ad_account_id && accountNeedsCatalog(account))
        : [];
    // La clave cambia cuando se elige una conversión nueva (el panel se refresca al guardar) y la
    // fila tiene que ir a buscar su nombre.
    const pendingKey = pending
        .map((account) => `${account.ad_account_id}:${unnamedSelectedActions(account).join(',')}`)
        .join('|');

    useEffect(() => {
        let cancelled = false;
        pending.forEach((account) => {
            const url = `${API_URL}agency/ad-accounts/${account.ad_account_id}/actions`;
            new APIClient(url).call('GET', {}, {cache: true}).then((response) => {
                if (cancelled || !isOk(response) || !Array.isArray(response.data)) return;
                const names = {};
                response.data.forEach((action) => {
                    const name = action.name || action.description;
                    if (name) names[action.action_id] = name;
                });
                setNamesByAccount((prev) => ({...prev, [account.id]: names}));
            }).catch(() => {});
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingKey]);

    return namesByAccount;
}

/**
 * Modal "Conversión que monitorea", que abre el chip del header del Panel.
 *
 * NO tiene endpoint propio: la conversión a nivel cliente se DERIVA de los eventos elegidos en cada
 * cuenta publicitaria (decisión de producto). Por eso el modal explica esa regla, muestra el
 * veredicto de hoy y manda a editar cuenta por cuenta reusando el `EditEventsModal` de la ficha
 * clásica — el mismo flujo, la misma validación y el mismo POST
 * (`client/ad_account/<id>/selected_actions`) que la pantalla de conexiones.
 *
 * Guardar en una cuenta puede cambiar el modo del cliente entero (de `ALL_PRIMARY` a `MIXED`, por
 * ejemplo), así que al éxito se refresca EL AGREGADO (`onSaved`), no sólo esta lista.
 */
export function ConversionModal({isOpen, toggle, conversion, adAccounts, onSaved}) {
    const {t} = useTranslation();
    // La cuenta que se está editando es a la vez el "abierto" del EditEventsModal y su parámetro.
    const [editing, setEditing] = useState(null);

    const accounts = paidAdAccounts(adAccounts);
    const actionNames = useCustomActionNames(isOpen, accounts);
    const currentLabel = monitoredConversionLabel(conversion, t);
    // La sugerencia por tipo de negocio ("Compras" en un ecommerce) sólo tiene sentido cuando nadie
    // eligió nada: con una conversión elegida a mano, sugerir otra es ruido.
    const anticipated = conversion && conversion.mode === MODE_ALL_PRIMARY && conversion.is_default
        ? conversion.anticipated_label
        : null;

    // Los dos caminos de guardado del modal (el `EditEventsModal` y la sugerencia de la cuenta sin
    // configurar) llegan acá sólo cuando el POST salió bien: el `EditEventsModal` llama `onSaved`
    // recién con `isOk(response)` (review C-app-05; antes lo llamaba con cualquier respuesta). El
    // aviso va acá y no en la capa de API porque el `EditEventsModal` es el de la ficha clásica:
    // encadenarlo ahí avisaría también desde
    // pantallas que no tienen el drawer de «Tus primeros pasos» (contrato alta-leo §4).
    const handleSaved = () => {
        toast.success(t('client_panel.conversion_saved', 'Conversión actualizada'));
        notifyOnboardingProgress({status: 'OK'});
        if (onSaved) onSaved();
    };

    // Una cuenta que no cuenta nada no es un renglón más: es la que deja las métricas de
    // conversión en cero. Se marca, y se le ofrece una salida en el lugar en vez de mandarla al
    // modal de eventos a decidir entre más de cien renglones.
    // La guía «Mostrarme cómo» de las conversiones (Aprende Dashbo) apunta al «Editar eventos» de la
    // PRIMERA cuenta que lo tiene: una sola fila marcada.
    const guideAccountId = (accounts.find((account) => (channelInfo(account) || {}).hasActions) || {}).id;

    const renderRow = (account) => {
        const info = channelInfo(account) || {};
        const unconfigured = accountHasNoEvents(account);
        return (
            <div className={`pnl-conv__row ${unconfigured ? '-unconfigured' : ''}`} key={account.id}>
                <div className="pnl-conv__row-main">
                    <ChannelLogo channel={normalizeChannel(account.channel)} height={18} width={18}
                                 hspace={0} vspace={0} marginLeft={0} padding={0}/>
                    <span className="pnl-conv__name">{account.name}</span>
                    <span className="pnl-conv__cfg">{accountConversionLabel(account, t, actionNames[account.id])}</span>
                    {info.hasActions && (
                        <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                                onClick={() => setEditing(account)}
                                {...(account.id === guideAccountId ? anchorAttr('conversion_edit_events') : {})}>
                            {t('client_panel.conversion_edit', 'Editar eventos')}
                        </button>
                    )}
                </div>
                {unconfigured && (
                    <UnconfiguredAccountSuggestion account={account} onSaved={handleSaved}
                                                   onOpenAll={() => setEditing(account)}/>
                )}
            </div>
        );
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} className="pnl-conv-modal">
                <ModalHeader toggle={toggle}>
                    {t('client_panel.conversion_title', 'Conversión que monitorea')}
                </ModalHeader>

                <ModalBody>
                    <div className="modal-section">
                        <p className="pnl-conv__explain">
                            {t('client_panel.conversion_explain',
                                'La conversión se define por cuenta y la toman todas las líneas de seguimiento '
                                + 'que la usan. Si una línea necesita medir otra cosa, puede tener la suya '
                                + 'propia en sus Fuentes de datos.')}
                        </p>

                        {currentLabel && (
                            <p className="pnl-conv__state">
                                {t('client_panel.conversion_current', 'Hoy monitorea')}{': '}
                                <strong>{currentLabel}</strong>
                            </p>
                        )}

                        {anticipated && (
                            <p className="pnl-conv__hint">
                                {t('client_panel.conversion_anticipated',
                                    'Sugerida para este negocio: {{label}}', {
                                        label: anticipated,
                                        interpolation: {escapeValue: false},
                                    })}
                            </p>
                        )}

                        {accounts.length ? (
                            <div className="pnl-conv__rows">{accounts.map(renderRow)}</div>
                        ) : (
                            <p className="pnl-conv__empty">
                                {t('client_panel.conversion_empty',
                                    'Este cliente todavía no tiene cuentas de paid media conectadas.')}
                            </p>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        {t('client_panel.conversion_close', 'Cerrar')}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* El modal de la ficha clásica, tal cual: cierra solo al guardar y avisa por `onSaved`. */}
            {editing && (
                <EditEventsModal
                    isOpen={!!editing}
                    toggle={() => setEditing(null)}
                    clientAdAccount={editing}
                    onSaved={handleSaved}
                />
            )}
        </>
    );
}
